import { ICountry } from "@directives/_forms/smsInput/ICountry";
import { IPhoneNumberOutput } from "@directives/_forms/smsInput/smsInput.interfaces";
import { DistanceMeasurement } from "@enums/measurements/DistanceMeasurement.enum";
import { AccountUserType } from "@enums/UserType";
import { IGenericPreference } from "@interfaces/IGenericPreference";
import { IHttpException } from "@interfaces/IHttpException";
import { IPreferencesContent } from "@interfaces/IPreferencesContent";
import { IRunBlockRootScope } from "@interfaces/IRunBlockRootScope";
import { IUser } from "@interfaces/IUser";
import { AccountService } from "@services/account.service";
import { AccountUserMappingService } from "@services/accountUserMapping.service.ajs";
import { JourneyCostType, PreferencesService } from "@services/preferences.service";
import { ITranslationLocale, TranslationService } from "@services/translation.service";
import { UsersService } from "@services/users.service";
import * as angular from "angular";
import copy from "fast-copy/dist/fast-copy.esm.js";
import { ITimeZoneInfo } from "./model/ITimeZoneInfo";
import "./userpreferences.scss";

export class UserPreferencesDirectiveController {

    currentPreferences: IPreferencesContent;
    accountPreferences: IPreferencesContent;
    userPreferences: IPreferencesContent;

    currentUser: IUser;
    countryCodes: ICountry[];
    timezones: ITimeZoneInfo[];
    locales: ITranslationLocale[];

    selectedTab: string;

    showMyPreferences: boolean = true;

    formIsValid: boolean = true;
    phoneIsValid: boolean = true;
    timezoneReadOnly: boolean;

    companyImage: string;

    saving: boolean = false;

    journeyCostTypeLabel: string;

    static $inject = ["$rootScope", "$mdDialog", "$q", "$window", "preferencesService", "accountService", "usersService", "accountUserMappingService", "translationService", "defaultLanguage"];

    constructor(
        private readonly $rootScope: IRunBlockRootScope,
        private readonly $mdDialog: angular.material.IDialogService,
        private readonly $q: angular.IQService,
        private readonly $window: angular.IWindowService,
        private readonly preferencesService: PreferencesService,
        private readonly accountService: AccountService,
        private readonly usersService: UsersService,
        private readonly accountUserMappingService: AccountUserMappingService,
        private readonly translationService: TranslationService,
        private readonly defaultLanguage: boolean
    ) {
    }

    $onInit() {
        this.initialise();

        this.currentUser = angular.copy(this.$rootScope.currentUser);
        if (this.defaultLanguage) {
            this.formIsValid = false;
        }
    }

    resetPreferences() {
        this.saving = true;

        this.userPreferences = {} as any;
        this.save();
    }

    hide(): void {
        this.$mdDialog.hide();
    }

    onSmsOutputChanged(output: IPhoneNumberOutput): void {
        if (output.input == '+44') {
            this.currentUser.CurrentAccountUserMapping.Phone = null;
            this.phoneIsValid = true;
            this.formIsValid = true;
            return;
        }

        if (output.input) {
            this.phoneIsValid = output.isPristine || output.isValid;
            this.formIsValid = this.phoneIsValid;
            this.currentUser.CurrentAccountUserMapping.Phone = output.internationalized;
        }
    }

    validateForm(valid: boolean) {
        if (valid) {
            this.formIsValid = valid;
        }
    }

    save() {
        this.saving = true;

        var promises = [];

        var userPrefToSave = copy(this.userPreferences);
        var accountPrefToSave = copy(this.accountPreferences);

        // Remove unchanged propterties to only save changed properties
        this.removeUnchangedProperties(userPrefToSave);

        promises.push(this.preferencesService.updateUserPreferences(userPrefToSave));

        if (this.currentUser.CurrentAccountUserMapping.Type == AccountUserType.Admin) {

            this.removeUnchangedProperties(accountPrefToSave);
            const updateAccountPrefPromise = this.preferencesService.updateAccountPreferences(accountPrefToSave);
            promises.push(updateAccountPrefPromise);
        }

        promises.push(this.usersService.save(this.currentUser).then(() => {
            return this.accountUserMappingService.postAccountUserMapping(this.currentUser.Email, this.currentUser.CurrentAccountUserMapping, null);
        }));

        var error = false;

        // Wait for all saving events to complete
        this.$q.all(promises)
            .catch((a: angular.IHttpPromiseCallbackArg<IHttpException>) => {
                error = true;
            })
            .finally(() => {
                this.saving = false;
                if (!error) {
                    this.$window.location.reload(true);
                }
            });
    }

    onPrefChanged(preference: IGenericPreference<any>) {
        preference.savePreference = true;

        this.updateJourneyCostTypeLabel();
    }

    showPasswordEditor($event: MouseEvent) {
        this.accountService.showPasswordEditor($event);
    }

    showChangeImageDialog(event: MouseEvent) {
        this.accountService.showMyCompanyImageDialog(event).then(() => {
            this.accountService.getAccountData(true).then(accountData => {
                this.companyImage = accountData.Image;
            })
        });
    }

    setPreferencesToAccountPrefs() {
        this.currentPreferences = this.accountPreferences;
        this.showMyPreferences = false;
    }

    setPreferencesToUserPrefs() {
        this.currentPreferences = this.userPreferences;
        this.showMyPreferences = true;
    }

    //------------------------------------------------

    private initialise() {
        var prefPromise = this.preferencesService.getAllPreferences();
        var companyDataPromise = this.accountService.getAccountData();
        var countryCodesPromise = this.preferencesService.getCountryCodes();
        var timezonesPromise = this.preferencesService.getTimezones();
        var localePromise = this.translationService.getListOfLocales();

        this.$q.all([prefPromise, companyDataPromise, countryCodesPromise, timezonesPromise, localePromise])
            .then(([prefs, companyData, countryCodes, timezones, locales]) => {

                this.accountPreferences = copy({ ...prefs.Default, ...prefs.Account });

                this.timezoneReadOnly = (prefs.Account?.Timezone != null);


                this.userPreferences = copy({ ...prefs.Default, ...prefs.Account, ...prefs.User });

                this.setExistingPrefProperties(prefs.Account, this.accountPreferences);
                this.setExistingPrefProperties(prefs.User, this.userPreferences);

                this.companyImage = companyData.Image;
                this.countryCodes = countryCodes;
                this.timezones = timezones;
                this.locales = locales.filter(locale => locale.Locale != "qa" && locale.Locale != "no");
            })
            .finally(() => {
                this.setPreferencesToUserPrefs();
                this.updateJourneyCostTypeLabel();
            });
    }

    private updateJourneyCostTypeLabel(): void {
        let translationPhrase: string;

        if (this.currentPreferences.JourneyCostType.Value == JourneyCostType.Distance) {
            if (this.currentPreferences.DistanceMeasurement.Value == DistanceMeasurement.Miles) {
                translationPhrase = "UserPreferences.costPerMile";

            } else if (this.currentPreferences.DistanceMeasurement.Value == DistanceMeasurement.Kilometres) {
                translationPhrase = "UserPreferences.costPerKilometre";

            }
        } else if (this.currentPreferences.JourneyCostType.Value == JourneyCostType.Hourly) {
            translationPhrase = "UserPreferences.costPerHour";
        }

        this.journeyCostTypeLabel = this.translationService.t(translationPhrase);
    }

    private removeUnchangedProperties(model: IPreferencesContent): void {
        for (const property in model) {
            const preference = model[property];

            if (preference && model[property].savePreference) {
                continue;
            }

            delete model[property];
        }
    }

    private setExistingPrefProperties(sourceModel: IPreferencesContent, targetModel: IPreferencesContent) {
        for (const property in sourceModel) {

            const preference = targetModel[property];
            if (!preference) {
                continue;
            }

            preference.savePreference = true;
        }
    }

    setSelectedTab(tab: string) {
        this.selectedTab = tab;
    }
}

export class UserPreferencesDirective implements angular.IDirective {
    restrict = "E";
    template = require("./userpreferences.html");
    controller = UserPreferencesDirectiveController;
    controllerAs = "$ctrl";
    bindToController = true;

    static factory(): angular.IDirectiveFactory {
        const directive = () => new UserPreferencesDirective();
        directive.$inject = [];
        return directive;
    }
}
